import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ItemThumbnail from '../../../components/ui/ItemThumbnail';
import BookingStatusBadge from './BookingStatusBadge';
import PaymentSummary from '../../../components/payments/PaymentSummary';
import ManualPaymentModal from '../../../components/payments/ManualPaymentModal';
import { useAuth } from '../../../components/context/AuthContext';
import { canPayAndCompleteManually, canRecordManualPayment } from '../../../components/payments/manualPaymentUtils';
import ConversationButton from '../../../components/chat/ConversationButton';
import ConversationModal from '../../../components/chat/ConversationModal';
import { hasConversationNote } from '../../../utils/chatAccess';
import FinalPriceEditor from '../../../components/pricing/FinalPriceEditor';
import { formatItemPrice, isRangePriceFinalized, isRangePriced, needsPriceFinalization } from '../../../utils/pricingUtils';
import repositoryContainer from '../../../services/RepositoryContainer';

const STATUS_ACTIONS = {
  pending: [
    { label: 'Confirm Booking', status: 'confirmed', variant: 'default' },
    { label: 'Cancel Booking', status: 'cancelled', variant: 'destructive' },
  ],
  confirmed: [
    { label: 'Start Service', status: 'inprogress', variant: 'default' },
    { label: 'No Show', status: 'noshow', variant: 'outline' },
    { label: 'Cancel Booking', status: 'cancelled', variant: 'destructive' },
  ],
  inprogress: [
    { label: 'Mark Completed', status: 'completed', variant: 'default' },
    { label: 'Cancel Booking', status: 'cancelled', variant: 'destructive' },
  ],
  completed: [],
  cancelled: [],
  noshow: [],
};

const normalizeStatus = (status) => String(status || '').toLowerCase().replace(/[\s_-]/g, '');

const formatDate = (value) => {
  if (!value) return 'Not provided';
  const date = new Date(value.length === 10 ? `${value}T00:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatDateTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

const formatTime = (value) => {
  if (!value) return 'Not provided';
  const [hours, minutes = '00'] = value.split(':');
  const hour = Number(hours);
  if (!Number.isFinite(hour)) return value;
  return `${hour % 12 || 12}:${minutes} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const formatAmount = (value) => `GH\u00A2${(Number(value) || 0).toFixed(2)}`;

const DETAIL_TONES = {
  primary: 'bg-primary/10 text-primary',
  secondary: 'bg-secondary/10 text-secondary',
  success: 'bg-success/10 text-success',
  warning: 'bg-warning/10 text-warning',
};

const DetailRow = ({ icon, label, value, tone = 'primary' }) => (
  <div className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
    <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg ${DETAIL_TONES[tone] || DETAIL_TONES.primary}`}>
      <Icon name={icon} size={19} />
    </div>
    <div className="min-w-0 pt-0.5">
      <p className="text-xs text-muted-foreground font-caption">{label}</p>
      <p className="mt-1 break-words text-sm text-foreground font-caption font-medium">{value || 'Not provided'}</p>
    </div>
  </div>
);

const BookingDetailsModal = ({ booking, onClose, onStatusChange, onPaymentRecorded, onPriceFinalized }) => {
  const { user } = useAuth();
  const [pendingActionStatus, setPendingActionStatus] = useState(null);
  const [paymentRecord, setPaymentRecord] = useState(booking);
  const [isManualPaymentOpen, setIsManualPaymentOpen] = useState(false);
  const [isConversationOpen, setIsConversationOpen] = useState(false);

  useEffect(() => {
    setPendingActionStatus(null);
    setPaymentRecord(booking);
    setIsManualPaymentOpen(false);
    setIsConversationOpen(false);
  }, [booking?.id, booking?.status, booking?.amountPaid, booking?.balanceDue, booking?.isFullyPaid]);

  useEffect(() => {
    if (!booking) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !pendingActionStatus && !isConversationOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [booking, isConversationOpen, onClose, pendingActionStatus]);

  const sourceServices = useMemo(() => {
    if (booking?.items?.length) return booking.items;
    if (booking?.serviceName) return [{ name: booking.serviceName, imageUrl: null }];
    return [];
  }, [booking?.items, booking?.serviceName]);
  const [services, setServices] = useState(sourceServices);

  useEffect(() => {
    setServices(sourceServices);
  }, [sourceServices]);

  if (!booking) return null;

  const normalizedStatus = normalizeStatus(booking.status);
  const totalAmount = paymentRecord?.totalAmount ?? booking.totalAmount ?? booking.price;
  const isAdmin = user?.roles?.some((role) => String(role).toLowerCase() === 'admin');
  const pricingPending = needsPriceFinalization({ ...booking, ...paymentRecord, items: services });
  const availableActions = isAdmin
    ? (STATUS_ACTIONS[normalizedStatus] || []).filter((action) => (
        !pricingPending || !['confirmed', 'inprogress', 'completed'].includes(action.status)
      ))
    : [];
  const canRecordBalance = canRecordManualPayment({
    isAdmin,
    record: { ...paymentRecord, items: services, requiresPriceFinalization: pricingPending },
    fallbackRequiresPayment: user?.company?.requiresPayment,
  });
  const canPayAndComplete = canRecordBalance && canPayAndCompleteManually({
    sourceType: 'booking',
    status: paymentRecord?.status ?? booking.status,
  });
  const bookingNote = booking.notes || booking.note;
  const hasExistingNote = hasConversationNote(booking.notes, booking.note, booking.companyNote);

  const handleActionClick = async (status) => {
    if (pendingActionStatus) return;
    setPendingActionStatus(status);
    try {
      await onStatusChange(booking.id, status);
    } finally {
      setPendingActionStatus(null);
    }
  };

  const handleFinalizePrice = async (serviceItem, finalUnitPrice) => {
    const serviceItemId = serviceItem.serviceItemId || serviceItem.id;
    const raw = await repositoryContainer.booking.finalizeItemPrice(booking.id, serviceItemId, finalUnitPrice);
    const result = raw?.response ?? raw;
    const updatedServices = services.map((item) => (
      (item.serviceItemId || item.id) === serviceItemId
        ? {
            ...item,
            unitPrice: result.finalUnitPrice,
            finalUnitPrice: result.finalUnitPrice,
            isPriceFinalized: true,
            priceFinalizedAt: result.priceFinalizedAt || new Date().toISOString(),
          }
        : item
    ));
    setServices(updatedServices);
    setPaymentRecord((current) => ({
      ...current,
      totalAmount: result.totalAmount,
      balanceDue: result.balanceDue,
      requiresPriceFinalization: updatedServices.some((item) => isRangePriced(item) && !isRangePriceFinalized(item)),
    }));
    await onPriceFinalized?.(booking.id, result);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm sm:p-4"
      onMouseDown={(event) => event.target === event.currentTarget && !pendingActionStatus && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-details-title"
        className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-border bg-card px-4 py-3 sm:px-6">
          <div>
            <h2 id="booking-details-title" className="font-heading text-xl font-semibold text-foreground">Booking Details</h2>
            <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
              <p className="text-xs text-muted-foreground font-caption">#{booking.referenceCode}</p>
              <BookingStatusBadge status={normalizedStatus} compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={Boolean(pendingActionStatus)}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close booking details"
          >
            <Icon name="X" size={20} />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 custom-scrollbar sm:p-6">
          <div className="space-y-6">
            <div className="grid items-stretch gap-4 md:grid-cols-[minmax(0,1.35fr)_minmax(260px,0.65fr)]">
              <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground font-caption">Customer information</p>
                  <h3 className="mt-1 font-heading text-2xl font-semibold text-foreground">{booking.clientName}</h3>
                  <div className="mt-3 flex flex-col gap-2 text-sm text-foreground font-caption sm:flex-row sm:flex-wrap sm:gap-x-5">
                    <span className="flex min-w-0 items-center gap-2">
                      <Icon name="Mail" size={15} className="flex-shrink-0 text-muted-foreground" />
                      <span className="break-all">{booking.clientEmail || 'Email not provided'}</span>
                    </span>
                    <span className="flex items-center gap-2">
                      <Icon name="Phone" size={15} className="flex-shrink-0 text-muted-foreground" />
                      <span>{booking.clientPhone || 'Phone not provided'}</span>
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground font-caption">
                    {services.length} {services.length === 1 ? 'service' : 'services'}
                    {booking.createdAt ? ` \u00B7 Submitted ${formatDateTime(booking.createdAt)}` : ''}
                  </p>
                </div>
              </div>
              </section>

              <section className="rounded-2xl bg-muted/30 p-4 sm:p-5">
                <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Payment</h4>
                {pricingPending && (
                  <div className="mb-3 rounded-lg border border-warning/20 bg-warning/10 p-3">
                    <p className="text-xs font-medium text-foreground font-caption">Finalize all range prices before taking payment or progressing this booking.</p>
                  </div>
                )}
                <PaymentSummary
                  record={paymentRecord}
                  totalAmount={totalAmount}
                  onRecordBalance={canRecordBalance ? () => setIsManualPaymentOpen(true) : undefined}
                />
              </section>
            </div>

            <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.8fr)]">
              <div className="space-y-6">
                <section>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <h4 className="text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Booked services</h4>
                    <span className="rounded-full bg-muted px-2.5 py-1 text-xs text-muted-foreground font-caption font-medium">{services.length}</span>
                  </div>

                  {services.length > 0 ? (
                    <div className="divide-y divide-border rounded-xl bg-muted/20 px-3">
                      {services.map((service, index) => (
                        <article key={service.serviceItemId || `${service.name}-${index}`} className="py-2.5">
                          <div className="flex items-center gap-3">
                            <ItemThumbnail src={service.imageUrl} alt={service.name} size="sm" fallbackIcon="Scissors" />
                            <div className="min-w-0 flex-1">
                              <p className="mb-1 text-sm font-semibold text-foreground font-caption">{service.name}</p>
                              <p className="text-xs text-muted-foreground font-caption">
                                Service{service.unitPrice != null || isRangePriced(service) ? ` \u00B7 ${formatItemPrice(service)}` : ''}
                              </p>
                              {isRangePriced(service) && (
                                <p className={`mt-1 text-xs font-caption ${isRangePriceFinalized(service) ? 'text-success' : 'text-warning'}`}>
                                  {isRangePriceFinalized(service) ? 'Final price confirmed' : 'Final price needed'}
                                </p>
                              )}
                            </div>
                          </div>
                          {isAdmin && isRangePriced(service) && !isRangePriceFinalized(service) && (
                            <FinalPriceEditor item={service} onFinalize={(value) => handleFinalizePrice(service, value)} />
                          )}
                        </article>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl bg-muted/30 p-6 text-center text-sm text-muted-foreground font-caption">
                      No service information is available.
                    </div>
                  )}
                </section>

                <section>
                  <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Booking history</h4>
                  {booking.history?.length > 0 ? (
                    <ol className="space-y-0 px-1 py-2">
                      {booking.history.map((event, index) => (
                        <li key={`${event.timestamp}-${index}`} className="relative flex gap-3 pb-5 last:pb-0">
                          {index < booking.history.length - 1 && (
                            <span className="absolute left-[5px] top-4 h-[calc(100%-0.25rem)] w-px bg-border" aria-hidden="true" />
                          )}
                          <span className="relative z-10 mt-1.5 h-3 w-3 flex-shrink-0 rounded-full border-2 border-card bg-primary" />
                          <div className="min-w-0">
                            <p className="text-sm text-foreground font-caption font-medium">{event.action}</p>
                            <p className="mt-1 text-xs text-muted-foreground font-caption">{formatDateTime(event.timestamp)}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <div className="rounded-xl bg-muted/30 p-5 text-center text-sm text-muted-foreground font-caption">
                      No booking history yet.
                    </div>
                  )}
                </section>
              </div>

              <aside className="space-y-5">
                <section className="rounded-xl bg-muted/25 p-4">
                  <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Appointment</h4>
                  <div className="divide-y divide-border">
                    <DetailRow icon="Calendar" label="Date" value={formatDate(booking.date)} tone="success" />
                    <DetailRow icon="Clock" label="Time" value={formatTime(booking.time)} tone="warning" />
                    <DetailRow icon="Banknote" label="Total" value={formatAmount(totalAmount)} />
                  </div>
                </section>

                {bookingNote && (
                  <section className="rounded-xl bg-muted/25 p-4">
                    <h4 className="mb-3 text-xs font-bold uppercase tracking-widest text-muted-foreground font-caption">Client notes</h4>
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground font-caption">{bookingNote}</p>
                  </section>
                )}
                <ConversationButton
                  hasNote={hasExistingNote}
                  onClick={() => setIsConversationOpen(true)}
                />
              </aside>
            </div>
          </div>
        </div>

        <footer className="flex flex-shrink-0 flex-col-reverse gap-3 border-t border-border bg-card px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button type="button" variant="outline" onClick={onClose} disabled={Boolean(pendingActionStatus)}>
            Close
          </Button>
          {availableActions.map((action) => (
            <Button
              key={action.status}
              variant={action.variant}
              onClick={() => handleActionClick(action.status)}
              loading={pendingActionStatus === action.status}
              disabled={Boolean(pendingActionStatus) && pendingActionStatus !== action.status}
            >
              {action.label}
            </Button>
          ))}
        </footer>
      </div>

      <ManualPaymentModal
        isOpen={isManualPaymentOpen}
        onClose={() => setIsManualPaymentOpen(false)}
        sourceType="booking"
        sourceId={booking.id}
        balanceDue={paymentRecord?.balanceDue}
        canPayAndComplete={canPayAndComplete}
        onRecorded={async (result) => {
          const updated = { ...paymentRecord, ...result };
          setPaymentRecord(updated);
          await onPaymentRecorded?.(booking.id, result);
        }}
      />

      <ConversationModal
        isOpen={isConversationOpen}
        onClose={() => setIsConversationOpen(false)}
        audience="staff"
        subjectType="booking"
        subjectId={booking.id}
        referenceCode={booking.referenceCode}
      />
    </div>
  );
};

export default BookingDetailsModal;
